import { createContext, useEffect, useReducer, useState } from "react";
import { useTenant } from "../hooks";
import axios from "axios";
import { toast } from "react-toastify";

const BalanceContext = createContext();

const getDefaultPagination = () => ({
  current_page: 1,
  last_page: 1,
  per_page: 15,
  total: 0,
});

const toPositiveInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
};

const extractBalancesPayload = (responseData) => {
  const payload = responseData?.data || {};
  const accountsPage = payload?.accounts || {};
  const data = Array.isArray(accountsPage.data) ? accountsPage.data : [];

  return {
    balances: data,
    pagination: {
      current_page: toPositiveInt(accountsPage.current_page) || 1,
      last_page: toPositiveInt(accountsPage.last_page) || 1,
      per_page: toPositiveInt(accountsPage.per_page) || 15,
      total: toPositiveInt(accountsPage.total) || 0,
    },
  };
};

const BalanceReducer = (state, action) => {
  switch (action.type) {
    case "SET_BALANCE":
      return action.payload;
    case "ADD_BALANCE":
      return [...state, action.payload];
    case "EDIT_BALANCE":
      return state.map((balance) =>
        balance.id === action.payload.id ? action.payload : balance,
      );
    case "DELETE_BALANCE":
      return state.filter((balance) => balance.id !== action.payload);
    default:
      return state;
  }
};

export const BalanceProvider = ({ children }) => {
  const { tenantId, companySelected } = useTenant();
  const BaseUrl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  const [balances, dispatch] = useReducer(BalanceReducer, []);
  const [pagination, setPagination] = useState(getDefaultPagination());
  const [loading, setLoading] = useState(false);
  const resolvePeriodId = () => {
    const period = JSON.parse(localStorage.getItem("selectedPeriod") || "{}");
    return period?.id || null;
  };
  const [periodId, setPeriodId] = useState(resolvePeriodId);

  const fetchBalances = async (options = {}) => {
    setLoading(true);
    try {
      if (!tenantId || !companySelected?.value || !periodId) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Skipping opening-balance fetch (missing context)", {
            tenantId,
            companyId: companySelected?.value,
            periodId,
          });
        }
        dispatch({ type: "SET_BALANCE", payload: [] });
        setPagination(getDefaultPagination());
        return null;
      }

      const requestPayload = {
        fin_year_id: periodId,
        tenant_id: tenantId,
        company_id: companySelected?.value,
      };

      const requestedPage = toPositiveInt(options?.page);
      const requestedPerPage = toPositiveInt(options?.perPage);
      if (requestedPage) requestPayload.page = requestedPage;
      if (requestedPerPage) requestPayload.per_page = requestedPerPage;
      const response = await axios.post(
        `${BaseUrl}/opening-balance`,
        requestPayload,
      );
      console.log(response);

      const { balances: balanceRows, pagination: pageInfo } =
        extractBalancesPayload(response?.data);

      dispatch({ type: "SET_BALANCE", payload: balanceRows });
      setPagination(pageInfo);

      return response?.data;
    } catch (error) {
      console.error("Failed to fetch balances:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchBalances();
  }, [tenantId, companySelected?.value, periodId]);

  useEffect(() => {
    const handlePeriodChange = () => {
      setPeriodId(resolvePeriodId());
    };
    window.addEventListener("periodChanged", handlePeriodChange);
    return () =>
      window.removeEventListener("periodChanged", handlePeriodChange);
  }, []);
  const addBalance = async (balanceData) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BaseUrl}/opening-balance/store`,
        balanceData,
      );

      console.log(response);
      if (response?.data?.status === 0) {
        toast.error(response.data.message);
      } else {
        dispatch({ type: "ADD_BALANCE", payload: response?.data?.data });
      }
      return response.data;
    } catch (error) {
      console.error("Failed to add balance:", error);
    } finally {
      setLoading(false);
    }
  };
  const editBalance = async (balanceData) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BaseUrl}/opening-balance/store`,
        balanceData,
      );
      console.log(balanceData);

      dispatch({ type: "EDIT_BALANCE", payload: response.data.data });
      fetchBalances();
      return response.data;
    } catch (error) {
      console.error("Failed to edit balance:", error);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const deleteBalance = async (balanceId) => {
    try {
      setLoading(true);
      await axios.get(`${BaseUrl}/opening-balance/delete/${balanceId}`);
      dispatch({ type: "DELETE_BALANCE", payload: balanceId });
    } catch (error) {
      console.error("Failed to delete balance:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BalanceContext.Provider
      value={{
        balances,
        pagination,
        loading,
        dispatch,
        addBalance,
        editBalance,
        deleteBalance,
        fetchBalances,
      }}
    >
      {children}
    </BalanceContext.Provider>
  );
};

export default BalanceContext;
